/**
 * Vorlagen und Kalkulation als zwei Reiter nebeneinander (Benjamin, 24.09.2026).
 *
 * Beide braucht man nur ab und zu — offen standen sie aber auf jedem Beleg
 * und schoben das Wichtige nach unten. Jetzt sind beide eingeklappt; ein Klick
 * öffnet einen, ein zweiter Klick auf denselben klappt ihn wieder zu. Es ist
 * immer höchstens einer offen.
 */
import { useState } from "react";

import { useVorlagen } from "../../hooks/useStammdaten";
import { euro, type WebInvoiceDraft } from "../../lib/invoices";
import { computeWebDraftTotals, kalkSumme } from "../../lib/invoiceEditor";
import InvoiceKalkulation from "./InvoiceKalkulation";
import InvoiceVorlagen from "./InvoiceVorlagen";

type Props = { draft: WebInvoiceDraft; onChange: (draft: WebInvoiceDraft) => void };
type Reiter = "" | "vorlagen" | "kalkulation";

export default function InvoiceWerkzeuge({ draft, onChange }: Props) {
  const [offen, setOffen] = useState<Reiter>("");
  const anzahl = useVorlagen().data?.items?.length ?? 0;
  const kosten = kalkSumme(draft.interne_kalk);
  // Im zugeklappten Reiter steht, ob sich das Aufklappen lohnt.
  const kalkHinweis = kosten > 0 ? `DB ${euro(computeWebDraftTotals(draft).net - kosten)}` : "nur intern";

  const reiter = (id: Exclude<Reiter, "">, titel: string, zusatz: string) => (
    <button
      type="button" role="tab" id={`werkzeug-${id}`} aria-selected={offen === id} aria-expanded={offen === id}
      aria-controls="werkzeug-inhalt" className={"invoice-werkzeug-reiter" + (offen === id ? " is-offen" : "")}
      onClick={() => setOffen((v) => (v === id ? "" : id))}
    >
      <span className="invoice-werkzeug-pfeil" aria-hidden="true">{offen === id ? "▾" : "▸"}</span>
      <strong>{titel}</strong>
      <small>{zusatz}</small>
    </button>
  );

  return (
    <section className={"invoice-editor-card invoice-werkzeuge" + (offen ? " is-offen" : "")}>
      <div className="invoice-werkzeug-reiterleiste" role="tablist" aria-label="Vorlagen und Kalkulation">
        {reiter("vorlagen", "Vorlagen", anzahl ? `${anzahl} gespeichert` : "wiederverwenden")}
        {reiter("kalkulation", "Kalkulation", kalkHinweis)}
      </div>
      {offen && (
        <div id="werkzeug-inhalt" role="tabpanel" aria-labelledby={`werkzeug-${offen}`}>
          {offen === "vorlagen"
            ? <InvoiceVorlagen draft={draft} onChange={onChange} eingebettet />
            : <InvoiceKalkulation draft={draft} onChange={onChange} eingebettet />}
        </div>
      )}
    </section>
  );
}
